/**
 * Meetings-sheet + client performance KPI tiles — grouped by funnel stage (Marketing · BDA · BDE).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    ArrowRight, RefreshCw, Table2, UserPlus, PhoneCall, BadgeCheck,
    CalendarCheck, Presentation, Target, IndianRupee, Megaphone,
    ShieldCheck, ShieldAlert, Users as UsersIcon,
} from 'lucide-react';
import api from '../utils/api';
import { inr } from '../utils/format';

const SECTION_META = {
    bdm: {
        title: 'BDM Team Rollup Gate',
        subtitle: 'PDF §4 — team must hit 90% of the cascade benchmark',
        header: 'bg-emerald-100 text-emerald-900 border-emerald-200',
        chip: 'bg-emerald-50 text-emerald-800',
        bar: 'bg-emerald-500',
        icon: ShieldCheck,
    },
    marketing: {
        title: 'Performance Marketing Agency',
        subtitle: 'Lead arrives → connect → qualify',
        header: 'bg-violet-100 text-violet-900 border-violet-200',
        chip: 'bg-violet-50 text-violet-800',
        bar: 'bg-violet-500',
        icon: Megaphone,
    },
    bda: {
        title: 'Business Development Associate (BDA)',
        subtitle: 'Schedule meeting → conduct meeting',
        header: 'bg-sky-100 text-sky-900 border-sky-200',
        chip: 'bg-sky-50 text-sky-800',
        bar: 'bg-sky-500',
        icon: CalendarCheck,
    },
    bde: {
        title: 'Business Development Executive (BDE)',
        subtitle: 'Conduct → conversion / MIB',
        header: 'bg-orange-100 text-orange-900 border-orange-200',
        chip: 'bg-orange-50 text-orange-800',
        bar: 'bg-orange-500',
        icon: Target,
    },
};

const KPI_ICONS = {
    fresh: UserPlus,
    connected: PhoneCall,
    qualified: BadgeCheck,
    scheduled: CalendarCheck,
    conducted: Presentation,
    conduct_to_mib: Target,
    org_collected: IndianRupee,
    collection: IndianRupee,
    crm_hygiene: BadgeCheck,
    /* H3 — BDM rollup gate icons */
    overall_funnel_pct:  ShieldCheck,
    bda_schedule_pct:    CalendarCheck,
    bda_conduct_pct:     Presentation,
    bde_conversion_pct:  Target,
};

function achievementTone(pct) {
    if (pct >= 90) return 'text-emerald-700 bg-emerald-50 border-emerald-200';
    if (pct >= 70) return 'text-brand-teal-700 bg-brand-teal-50 border-brand-teal-200';
    if (pct >= 50) return 'text-amber-700 bg-amber-50 border-amber-200';
    return 'text-rose-700 bg-rose-50 border-rose-200';
}

function barTone(pct) {
    if (pct >= 90) return 'bg-emerald-500';
    if (pct >= 70) return 'bg-brand-teal-500';
    if (pct >= 50) return 'bg-amber-500';
    return 'bg-rose-500';
}

function sectionAvg(kpis) {
    const scored = kpis.filter(k => k.achievementPct != null && !k.isCurrency && !Number.isNaN(k.achievementPct));
    if (!scored.length) return null;
    return Math.round(scored.reduce((a, k) => a + k.achievementPct, 0) / scored.length);
}

function FunnelStrip({ funnel, leads, totalLeads }) {
    /* Prefer live leadStats so headings stay correct even if an older funnel payload is cached. */
    const byKey = Object.fromEntries((funnel || []).map((s) => [s.key, s]));
    const steps = [
        {
            key: 'total',
            label: 'Total leads',
            actual: leads?.total ?? totalLeads ?? byKey.total?.actual ?? 0,
            tone: 'teal',
        },
        {
            key: 'not_contacted',
            label: 'Not contacted',
            actual: leads?.notContacted ?? byKey.not_contacted?.actual ?? 0,
            tone: 'default',
        },
        {
            key: 'not_connected',
            label: 'Not connected',
            actual: leads?.notConnected ?? byKey.not_connected?.actual ?? 0,
            tone: 'amber',
        },
        {
            key: 'connected',
            label: 'Connected',
            actual: leads?.meetingConnected ?? byKey.connected?.actual ?? 0,
            tone: 'default',
        },
        {
            key: 'in_pipeline',
            label: 'In pipeline',
            actual: leads?.inPipeline ?? byKey.in_pipeline?.actual ?? 0,
            tone: 'teal',
        },
        {
            key: 'closed_lost',
            label: 'Closed lost',
            actual: leads?.closedLost ?? leads?.lost ?? byKey.closed_lost?.actual ?? 0,
            tone: 'rose',
        },
    ];

    const toneCls = {
        teal: 'ring-brand-teal/15 bg-brand-teal-50/30',
        amber: 'ring-amber-100 bg-amber-50/40',
        rose: 'ring-rose-100 bg-rose-50/40',
        default: 'ring-ink-100/80 bg-white',
    };

    return (
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
            {steps.map((step) => (
                <div
                    key={step.key}
                    className={`rounded-xl px-3 sm:px-4 py-3 ring-1 ${toneCls[step.tone] || toneCls.default}`}
                >
                    <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-500 truncate">
                        {step.label}
                    </div>
                    <div className="mt-1 text-xl font-bold text-ink-900 tabular-nums">
                        {Number(step.actual || 0).toLocaleString('en-IN')}
                    </div>
                </div>
            ))}
        </div>
    );
}

function KpiCard({ kpi }) {
    const Icon = KPI_ICONS[kpi.key] || Target;
    const pct = kpi.isCurrency ? null : (kpi.achievementPct ?? null);
    const hasTarget = kpi.target != null && kpi.target > 0 && !kpi.isCurrency;
    const progress = hasTarget ? Math.min(100, Math.round((Number(kpi.actual) / Number(kpi.target)) * 100)) : null;

    /* H5 — cascade derivation sub-line. When the backend supplied cascadeBase
       + frameworkPct, render the math explicitly:
         "50% of 2931 Fresh = 1465 target"
       That way a sales user / admin can see exactly where the target came
       from. We hide it for the "fresh" tile (it has no upstream stage) and
       for currency / non-cascade rows. */
    const showCascade = kpi.cascadeBase != null
        && kpi.cascadeBaseLabel
        && kpi.frameworkPct != null
        && kpi.key !== 'fresh'
        && !kpi.isCurrency;

    return (
        <div className="ag-card p-4 flex flex-col h-full hover:ring-2 hover:ring-brand-teal/20 transition">
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className={`p-2 rounded-lg shrink-0 ${SECTION_META[kpi.section]?.chip || 'bg-ink-50 text-ink-700'}`}>
                    <Icon className="h-4 w-4" />
                </div>
                {kpi.frameworkPct != null && kpi.key !== 'fresh' && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-ink-100 text-ink-600">
                        Target rate {kpi.frameworkPct}%
                    </span>
                )}
            </div>
            <div className="text-[11px] uppercase tracking-wide text-ink-500 font-semibold line-clamp-2 min-h-[2rem]">
                {kpi.label}
            </div>
            <div className="text-2xl font-extrabold mt-1 tabular-nums">
                {kpi.isCurrency ? inr(kpi.actual) : `${kpi.actual}${kpi.suffix || ''}`}
            </div>
            {showCascade && (
                <div className="text-[10px] text-ink-500 mt-1 leading-tight">
                    <b className="text-ink-700">{kpi.frameworkPct}%</b> of
                    {' '}<b className="text-ink-700 tabular-nums">{Number(kpi.cascadeBase).toLocaleString('en-IN')}</b>
                    {' '}{kpi.cascadeBaseLabel}
                    {' = '}
                    <b className="text-ink-700 tabular-nums">{kpi.target}</b> target
                </div>
            )}
            {hasTarget && (
                <>
                    <div className="mt-3 h-1.5 rounded-full bg-ink-100 overflow-hidden">
                        <div
                            className={`h-full rounded-full transition-all ${barTone(pct ?? progress ?? 0)}`}
                            style={{ width: `${Math.min(100, progress ?? 0)}%` }}
                        />
                    </div>
                    <div className="flex items-center justify-between mt-2 text-xs gap-2">
                        <span className="text-ink-500 truncate">
                            Target {kpi.isCurrency ? inr(kpi.target) : kpi.target}
                        </span>
                        {pct != null && (
                            <span className={`font-bold px-1.5 py-0.5 rounded border shrink-0 ${achievementTone(pct)}`}>
                                {pct}%
                            </span>
                        )}
                    </div>
                </>
            )}
            {kpi.isCurrency && (
                <div className="text-xs text-ink-500 mt-2">Revenue collected this period</div>
            )}
            {!hasTarget && !kpi.isCurrency && pct != null && (
                <div className="flex justify-end mt-2">
                    <span className={`font-bold px-1.5 py-0.5 rounded border text-xs ${achievementTone(pct)}`}>
                        {pct}%
                    </span>
                </div>
            )}
        </div>
    );
}

function SectionBlock({ section, kpis }) {
    const meta = SECTION_META[section];
    if (!meta || !kpis.length) return null;
    const avg = sectionAvg(kpis);
    const Icon = meta.icon;
    const cols = section === 'bda' ? 'sm:grid-cols-2' : section === 'bde' ? 'sm:grid-cols-2 lg:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3';

    return (
        <div className="ag-card overflow-hidden">
            <div className={`px-4 py-3 border-b flex flex-wrap items-center justify-between gap-2 ${meta.header}`}>
                <div className="flex items-center gap-2 min-w-0">
                    <Icon className="h-4 w-4 shrink-0" />
                    <div>
                        <div className="font-bold text-sm">{meta.title}</div>
                        <div className="text-[10px] opacity-80">{meta.subtitle}</div>
                    </div>
                </div>
                {avg != null && (
                    <span className={`text-xs font-bold px-2 py-1 rounded-full border border-current/20 ${achievementTone(avg)}`}>
                        Section KPI {avg}%
                    </span>
                )}
            </div>
            <div className={`p-3 grid grid-cols-1 ${cols} gap-3`}>
                {kpis.map(kpi => (
                    <KpiCard key={kpi.key} kpi={kpi} />
                ))}
            </div>
        </div>
    );
}

/* H3 — pass/fail summary banner shown above the BDM rollup section. */
function BdmGateBanner({ gate }) {
    if (!gate) return null;
    const allPass = !!gate.allPass;
    const tone = allPass
        ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
        : 'bg-amber-50 border-amber-200 text-amber-900';
    const Icon = allPass ? ShieldCheck : ShieldAlert;
    return (
        <div className={`ag-card p-4 border ${tone} flex flex-wrap items-center justify-between gap-3`}>
            <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-white/60 shrink-0"><Icon className="h-5 w-5" /></div>
                <div>
                    <div className="font-extrabold text-sm">
                        {allPass
                            ? 'Team gate cleared'
                            : `Team gate — ${gate.passing}/${gate.total} KPIs at benchmark`}
                    </div>
                    <div className="text-xs opacity-80 mt-0.5">
                        PDF §4 rollup · {gate.cadence} cadence · team of {gate.teamSize}
                        {gate.teamSize === 1 ? '' : ' members'}
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-2 text-xs">
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/70 border border-current/20 font-semibold">
                    <UsersIcon className="h-3.5 w-3.5" /> {gate.teamSize}
                </span>
                <span className={`px-2 py-1 rounded-full border font-bold ${allPass ? 'bg-emerald-100 border-emerald-300' : 'bg-amber-100 border-amber-300'}`}>
                    {gate.passing}/{gate.total} passing
                </span>
            </div>
        </div>
    );
}

export default function PerformanceKpiTiles({
    scope = 'personal',
    title = 'Performance KPIs',
    embedded = false,
    showFunnel = true,
    hideActions = false,
    dateParams = null,
    periodLabel: periodLabelProp = '',
    officeBranch = 'all',
}) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');

    const load = () => {
        setLoading(true);
        setErr('');
        const params = {
            ...(scope === 'org' ? { scope: 'org' } : {}),
            ...(dateParams || {}),
            ...(officeBranch && officeBranch !== 'all' ? { officeBranch } : {}),
            _: Date.now(),
        };
        api.get('/dashboard/kra-kpi', { params, timeout: 120_000 })
            .then(r => {
                if (r.status === 304 || r.data == null) return;
                setData(r.data);
            })
            .catch(e => {
                const timedOut = e.code === 'ECONNABORTED' || /timeout/i.test(e.message || '');
                setErr(
                    timedOut
                        ? 'KPI overview timed out — try again or open KPI analytics with filters.'
                        : (e.response?.data?.message || 'Could not load KPIs'),
                );
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, [scope, JSON.stringify(dateParams), officeBranch]);

    const kpis = data?.kpis || [];
    const grouped = useMemo(() => ({
        bdm: kpis.filter(k => k.section === 'bdm'),
        marketing: kpis.filter(k => k.section === 'marketing'),
        bda: kpis.filter(k => k.section === 'bda'),
        bde: kpis.filter(k => k.section === 'bde'),
        other: kpis.filter(k => !['bdm', 'marketing', 'bda', 'bde'].includes(k.section)),
    }), [kpis]);

    const bdmGate = data?.bdmGate || null;

    const leadsLink = scope === 'org'
        ? '/admin'
        : (data?.links?.teamLeads || data?.links?.leads || '/sales/leads');
    const analyticsLink = scope === 'org' ? '/admin/kpi-analytics' : '/sales/kpi-analytics';
    const leadsLabel = scope === 'org' ? 'Daily meetings' : (data?.role === 'bdm' ? 'Team leads' : 'My leads');

    const header = (
        <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
                {!embedded && <h2 className="font-heading text-lg font-extrabold">{title}</h2>}
                {embedded && <h2 className="font-heading text-base font-extrabold">KPI summary</h2>}
                <p className="text-xs text-ink-500 mt-0.5">
                    {(periodLabelProp || data?.period?.label) && `${periodLabelProp || data?.period?.label} · `}
                    {data?.roleLabel || 'Sales'} · meetings sheet + clients
                </p>
            </div>
            {!hideActions && (
                <div className="flex gap-2">
                    <button type="button" onClick={load} disabled={loading} className="ag-btn-ghost text-xs">
                        <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
                    </button>
                    {!embedded && (
                        <>
                            <Link to={analyticsLink} className="ag-btn-ghost text-xs">
                                <Table2 className="h-3.5 w-3.5" /> Advanced table
                            </Link>
                            <Link to={leadsLink} className="ag-btn-ghost text-xs">
                                {leadsLabel} <ArrowRight className="h-3.5 w-3.5" />
                            </Link>
                        </>
                    )}
                </div>
            )}
        </div>
    );

    return (
        <section className={embedded ? 'space-y-3' : 'space-y-4 pt-4 border-t border-ink-100'}>
            {header}

            {loading && (
                <div className="ag-card p-8 flex flex-col items-center justify-center gap-3 text-ink-500">
                    <RefreshCw className="h-6 w-6 animate-spin text-brand-teal" />
                    <p className="text-sm font-medium">Loading KPI summary…</p>
                </div>
            )}

            {!loading && err && !kpis.length && (
                <div className="ag-card p-4 text-sm text-rose-700 bg-rose-50 border border-rose-100">
                    {err}
                </div>
            )}

            {!loading && data?.zoho?.ok === false && data?.zoho?.error && (
                <div className="ag-card p-3 text-sm text-amber-900 bg-amber-50 border border-amber-100">
                    Could not load the meetings sheet — {data.zoho.error}
                </div>
            )}

            {!loading && data?.zoho?.truncated && (
                <div className="ag-card p-3 text-sm text-amber-900 bg-amber-50 border border-amber-100">
                    Some users have very large lead volumes — counts may be truncated.
                </div>
            )}

            {!loading && kpis.length > 0 && (
                <div className="space-y-4">
                    {showFunnel && (
                        <FunnelStrip
                            funnel={data?.funnel}
                            leads={data?.leads}
                            totalLeads={data?.totalLeads ?? data?.leads?.total}
                        />
                    )}

                    {bdmGate && <BdmGateBanner gate={bdmGate} />}

                    <div className="space-y-4">
                        {['bdm', 'marketing', 'bda', 'bde'].map(section => {
                            const group = grouped[section];
                            if (!group.length) return null;
                            return <SectionBlock key={section} section={section} kpis={group} />;
                        })}
                    </div>

                    {grouped.other.length > 0 && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                            {grouped.other.map(kpi => (
                                <KpiCard key={kpi.key} kpi={{ ...kpi, section: 'bde' }} />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}
